import React from "react";

import { useIntl } from "react-intl";

import * as Sticker from "ds/Sticker";

import * as S from "./ResourceStickers.styles";

import type { TrialResourceLimitsType } from "src/organization/pages/project/environment/ConfigureResourcesPage/types";

export const InactiveEnvironmentWarning = () => {
  const intl = useIntl();

  return (
    <S.PermissionsStickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.inactive.environment.warning",
          defaultMessage:
            "This environment is inactive. Making a change to the resources will also activate this environment."
        })}
      </Sticker.Text>
    </S.PermissionsStickerRoot>
  );
};

export const AdminUpdateResourcesWarning = () => {
  const intl = useIntl();

  return (
    <S.PermissionsStickerRoot priority="information">
      <Sticker.Icon priority="information" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.admin.update.resources.warning",
          defaultMessage:
            "Ask your project admin to update the resources on this environment."
        })}
      </Sticker.Text>
    </S.PermissionsStickerRoot>
  );
};

type ErrorAlertProps = {
  message: string;
};
export const ErrorAlert = ({ message }: ErrorAlertProps) => (
  <S.StickerRoot priority="critical">
    <Sticker.Icon priority="critical" />
    <Sticker.Text>{message}</Sticker.Text>
  </S.StickerRoot>
);

export const BillingWarning = () => {
  const intl = useIntl();
  return (
    <S.StickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.billing.warning",
          defaultMessage:
            "To configure your project resources you need to add your billing details as your free trial has expired."
        })}
      </Sticker.Text>
    </S.StickerRoot>
  );
};

export const ReduceWarning = () => {
  const intl = useIntl();

  return (
    <S.ReduceStickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.reduce.warning"
        })}
      </Sticker.Text>
    </S.ReduceStickerRoot>
  );
};

export const NoResourcesWarning = () => {
  const intl = useIntl();

  return (
    <S.NoResourceStickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage(
          {
            id: "configure_resources.page.no_resources.warning"
          },
          {
            link: chucks => (
              <S.Link
                variant="link"
                href={intl.formatMessage({
                  id: "links.documentation.resources.configuration"
                })}
              >
                {chucks}
              </S.Link>
            )
          }
        )}
      </Sticker.Text>
    </S.NoResourceStickerRoot>
  );
};

type TrialResourcesExceededWarningProps = {
  trialResourceLimits: TrialResourceLimitsType;
};
export const TrialResourcesExceededWarning = ({
  trialResourceLimits
}: TrialResourcesExceededWarningProps) => {
  const intl = useIntl();

  return (
    <S.StickerRoot priority="critical">
      <Sticker.Icon priority="critical" />
      <Sticker.Text>
        {intl.formatMessage(
          {
            id: "configure_resources.page.trial_resources.warning"
          },
          trialResourceLimits
        )}
      </Sticker.Text>
    </S.StickerRoot>
  );
};

export const ResourceUpdateInProgressWarning = () => {
  const intl = useIntl();

  return (
    <S.PermissionsStickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.resources.update.in_progress.warning",
          defaultMessage:
            "A resource update activity is currently in progress. To change resources on this environment, please check back after that activity has completed."
        })}
      </Sticker.Text>
    </S.PermissionsStickerRoot>
  );
};
